import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import ActivityManager from '../../managers/ActivityManager'
import { useAppSelector } from '../../store/Hooks'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'
import SmartTable from '../controls/table/SmartTable'
import { Box, Chip, Pagination } from '@mui/material'
import { useState } from 'react'
import { IPagination } from '../../definitions/Common'
import { changeRoute } from '../../store/slices/SessionSlice'
import { SmartTableUtilities } from '../controls/table'

export default function ProviderContracts() {
    const dispatch = useDispatch()
    const params = RoutingManager.getParams<{ providerId: number }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [pagination, setPagination] = useState<IPagination>({ page: 0, batch: 20 })

    const { status, response } = useQuery({
        queryKey: buildQueryKey('getProviderKPI', params.providerId, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getProviderKPI(params.providerId, time),
        cache: true
    })

    const { status: contractsStatus, response: contractsResponse } = useQuery({
        queryKey: buildQueryKey('contracts', 'counterparty.providers'),
        queryFn: async () => await ApiClient.getAllContracts(['counterparty.providers']),
        cache: true
    })

    const isLoading = status === 'loading' || contractsStatus === 'loading'
    const filteredConctracts = (contractsResponse ?? [])?.filter(x => x?.counterparty.providers?.some(k => k.id === params.providerId))
    const numberContrats = filteredConctracts?.length ?? 0

    return (
        <Tile.Container>
            <RouteHeader
                isLoading={isLoading}
                primary={response?.provider?.name} // response?.name
                timeSettings={time}
                subActions={[{ label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('providers') }]}
            />
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1}>
                    <KPICard
                        label='Quality Measures avg. Scores'
                        isLoading={isLoading}
                        unit='%'
                        data={Helper.dataframeToKPICard(response?.kpi?.avgScores)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Attributed Members'
                        isLoading={isLoading}
                        data={Helper.dataframeToKPICard(response?.kpi?.attributedMembers)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Total Claims ($)'
                        isLoading={isLoading}
                        inverse
                        unit='$'
                        data={Helper.dataframeToKPICard(response?.kpi?.totalPaymentClaims)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Total Claims (#)'
                        inverse
                        isLoading={isLoading}
                        unit='#'
                        data={Helper.dataframeToKPICard(response?.kpi?.totalClaims)}
                    />
                </SmartCard>
            </Tile.Row>
            <SmartCard style={{ overflowY: 'auto', height: 'auto', maxHeight: '100%' }}>
                <SmartTable
                    columns={[
                        { id: 'name', label: 'Contract Name' },
                        { id: 'counterparty.name', label: 'Entity' },
                        { id: 'startDate', label: 'Start date' },
                        { id: 'status', label: 'Status' },
                        {
                            id: 'type', label: 'Type', customRenderer: x => {
                                return <Chip style={{ fontWeight: 600 }} label={x.value.toString()} />
                            }
                        },
                        { id: 'lineofbusiness', label: 'Line of Business' }
                    ]}
                    isLoading={status === 'loading'}
                    noCollapse
                    onClickRow={i => dispatch(changeRoute({ route: 'provider', params: { providerId: params.providerId, contractId: filteredConctracts[i].id } }))}
                    datasource={{
                        rows: filteredConctracts?.map((x, i) => SmartTableUtilities.toRow([
                            { value: x?.name },
                            { value: x?.counterparty?.name },
                            { value: new Date(x.startDate).toLocaleDateString() },
                            { value: x.status?.name },
                            { value: x.type?.name },
                            { value: x.lineOfBusinesses?.map(x => x.name)?.join(', ') }

                        ], i))
                    }}
                />
                <Box display='flex' justifyContent='flex-end'>
                    <Pagination
                        key={pagination.page}
                        count={Math.ceil(numberContrats / pagination.batch)}
                        defaultPage={(pagination.page + 1)}
                        color='primary'
                        sx={{ marginTop: 1 }}
                        disabled={isLoading}
                        onChange={(_, value) => setPagination(p => {
                            return { batch: p.batch, page: value - 1 }
                        })}
                    />
                </Box>
            </SmartCard>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
